/**
 * Every chat request in one place: the CSRF header, the conversation id, JSON parsing and one
 * error shape. A component never builds a fetch of its own, so a new endpoint can't quietly
 * forget the token or the "read the message out of the error body" step.
 */
export type ChatApiConfig = {
    csrfToken: string;
    conversationId: string;
    sendUrl: string;
    resetUrl: string;
    verifyReturnUrl: string;
    submitReturnUrl: string;
    verifyOrderStatusUrl: string;
};

export type {ActiveFilter, ReturnLine} from './chatTypes';
import type {ActiveFilter, ReturnLine, ChatResponse, ResetResponse} from './chatTypes';
import type {CompareRequest} from '../../Store';

/** What the transcript shows for a comparison, whichever list the products were ticked in. */
export const compareMessage = (productIds: string[]) => `Vergelijk deze ${productIds.length} producten`;

export function createChatApi(config: ChatApiConfig) {
    async function post<T = ChatResponse>(url: string, body: Record<string, unknown>, fallbackMessage: string): Promise<T> {
        let response: Response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': config.csrfToken,
                    Accept: 'application/json',
                },
                body: JSON.stringify({conversationId: config.conversationId, ...body}),
            });
        } catch (error) {
            throw new Error('Geen verbinding met de server. Probeer het opnieuw.');
        }

        let payload: any = {};
        try {
            payload = await response.json();
        } catch (error) {
            payload = {};
        }

        if (!response.ok) {
            if (response.status === 422 && payload.errors) {
                // Only the customer's own text has errors written for the customer; any other
                // field's validation text is technical, so it gets this action's fallback.
                throw new Error(payload.errors.message?.[0] || fallbackMessage);
            }
            throw new Error(payload.message || fallbackMessage);
        }

        return payload as T;
    }

    return {
        send: (message: string, choiceId?: string | null) =>
            post(config.sendUrl, choiceId ? {message, choiceId} : {message}, 'Er is een fout opgetreden.'),
        removeFilter: (filter: ActiveFilter) =>
            post(
                config.sendUrl,
                {
                    message: `Verwijder filter ${filter.label}: ${filter.values.join(', ')}`,
                    filterAction: 'remove',
                    facetKey: filter.facetKey,
                },
                'De filter kon niet worden verwijderd.'
            ),
        reset: () => post<ResetResponse>(config.resetUrl, {}, 'Resetten is mislukt.'),
        compare: (productIds: string[]) =>
            post(
                config.sendUrl,
                {message: compareMessage(productIds), action: 'compare', productIds},
                'Vergelijken lukte niet. Kies de producten opnieuw en probeer het nog eens.'
            ),
        comparePageProducts: ({productIds, typeCode}: CompareRequest) =>
            post(
                config.sendUrl,
                {
                    message: compareMessage(productIds),
                    action: 'compare_page_products',
                    productIds,
                    ...(typeCode !== null ? {listedTypeCode: typeCode} : {}),
                },
                'Vergelijken lukte niet. Kies de producten opnieuw en probeer het nog eens.'
            ),
        cancelReturn: () => post(config.sendUrl, {message: 'Ik annuleer de retour.', action: 'cancel_return'}, 'Annuleren is mislukt.'),
        verifyReturn: (orderId: string, emailAddress: string) =>
            post(config.verifyReturnUrl, {orderId, emailAddress}, 'Er is een fout opgetreden.'),
        submitReturn: (orderLines: ReturnLine[]) => post(config.submitReturnUrl, {orderLines}, 'Er is een fout opgetreden.'),
        verifyOrderStatus: (orderId: string, emailAddress: string) =>
            post(config.verifyOrderStatusUrl, {orderId, emailAddress}, 'Er is een fout opgetreden.'),
    };
}
